import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Brain, ChevronDown } from 'lucide-react';
import { memo } from 'react';

import type { AiChatAttachmentRef } from '@/domain/repositories/ai-chat-repository.js';
import { EfChatEfAttachment } from '@/presentation/pages/developments/chat/ef-chat-ef-attachment.js';
import { EfErrorMessage } from '@/presentation/pages/developments/chat/ef-error-message.js';
import { EfMarkdownContent } from '@/presentation/pages/developments/chat/ef-markdown-content.js';
import { resolveErrorContent } from '@/presentation/pages/developments/chat/utils/resolve-error-content.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

export type EfChatMessageView = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    attachments?: AiChatAttachmentRef[];
    reasoning?: string;
    status: 'streaming' | 'complete' | 'error';
};

export type EfChatMessageProps = {
    message: EfChatMessageView;
    streamingLabel?: string;
    errorLabel?: string;
    errorTitleLabel?: string;
    errorMessages?: Record<string, string>;
    reasoningLabel?: string;
    attachmentDownloadLabel?: string;
    onDownloadAttachment?: (fileUrl: string) => void;
};

function ReasoningBlock({ reasoning, label }: { reasoning: string; label: string }) {
    return (
        <Accordion
            disableGutters
            elevation={0}
            square
            sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ChevronDown size={14} style={{ opacity: 0.5 }} />} sx={{ minHeight: 0, px: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Brain size={13} style={{ opacity: 0.45 }} />
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontStyle: 'italic' }}>{label}</Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 0, pb: 0.5 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled', whiteSpace: 'pre-wrap', lineHeight: 1.55, pl: 0.25, borderLeft: '2px solid', borderColor: 'divider' }}>
                    <Box component="span" sx={{ display: 'block', pl: 1.25 }}>
                        {reasoning}
                    </Box>
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
}

function EfChatMessageImpl({
    message,
    streamingLabel = 'Pensando…',
    errorLabel = 'Erro ao gerar resposta',
    errorTitleLabel = 'Erro na análise',
    errorMessages,
    reasoningLabel = 'Raciocínio',
    attachmentDownloadLabel = 'Baixar anexo',
    onDownloadAttachment
}: EfChatMessageProps) {
    const isUser = message.role === 'user';

    if (message.status === 'error') {
        return (
            <EfErrorMessage
                id={message.id}
                content={resolveErrorContent(message.content, errorMessages)}
                errorLabel={errorLabel}
                errorTitleLabel={errorTitleLabel}
                isUser={isUser}
            />
        );
    }

    if (isUser) {
        return (
            <Box
                data-testid={`ef-chat-message-${message.id}`}
                sx={{
                    alignSelf: 'flex-end',
                    maxWidth: '85%',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1.25
                }}
            >
                <Typography sx={{ fontSize: 14, color: 'text.primary', whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
                {message.attachments && message.attachments.length > 0 ? (
                    <Stack spacing={1} sx={{ mt: 1 }} data-testid="ef-chat-message-attachments">
                        {message.attachments.map((attachment, index) => (
                            <EfChatEfAttachment
                                key={attachment.fileUrl ?? index}
                                fileName={attachment.fileName}
                                metaLabel={attachment.fileSizeBytes !== undefined ? formatFileSize(attachment.fileSizeBytes) : undefined}
                                downloadLabel={attachmentDownloadLabel}
                                onDownload={onDownloadAttachment ? () => onDownloadAttachment(attachment.fileUrl) : undefined}
                            />
                        ))}
                    </Stack>
                ) : null}
            </Box>
        );
    }

    return (
        <Stack spacing={0.5} data-testid={`ef-chat-message-${message.id}`} sx={{ alignSelf: 'flex-start', width: '100%' }}>
            {message.reasoning ? <ReasoningBlock reasoning={message.reasoning} label={reasoningLabel} /> : null}
            {message.content ? <EfMarkdownContent content={message.content} /> : null}
            {message.status === 'streaming' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={12} thickness={5} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{streamingLabel}</Typography>
                </Stack>
            ) : null}
        </Stack>
    );
}

export const EfChatMessage = memo(EfChatMessageImpl);
